import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GenericApiErrorComponent } from '@shared/components/error/generic-api-error/generic-api-error.component';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { IMemberProfileTraveller } from '@shared/shared/interfaces/member-profile-traveller.interfaces';
import * as moment from 'moment';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as MemberProfileTravellersActions from '../actions/member-profile-travellers.actions';
import { MemberProfileTravellersAction } from '../actions/member-profile-travellers.actions';
import { MemberProfileState } from '../reducers/member-profile.reducers';
import { getMemberProfileFeatureState } from '../selectors/member-profile.selectors';

@Injectable()
export class MemberProfileTravellerEffects {
  constructor(
    private actions$: Actions<MemberProfileTravellersAction>,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private store: Store<{ 'member-profile': MemberProfileState }>
  ) {}

  public createProfileTraveller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfileTravellersActions.CREATE_PROFILE_TRAVELLER),
      switchMap((action: MemberProfileTravellersActions.CreateProfileTraveller) => {
        return this.http.post<IMemberProfileTraveller>(ApiEndpoint.MEMBER_PROFILE_TRAVELLER, action.payload).pipe(
          map((traveller) => new MemberProfileTravellersActions.CreateProfileTravellerSuccess(traveller)),
          catchError(() => of(new MemberProfileTravellersActions.CreateProfileTravellerFail()))
        );
      })
    )
  );

  public destroyProfileTraveller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfileTravellersActions.DESTROY_PROFILE_TRAVELLER),
      switchMap((action: MemberProfileTravellersActions.DestroyProfileTraveller) => {
        const url = `${ApiEndpoint.MEMBER_PROFILE_TRAVELLER}${action.payload}/`;

        return this.http.delete(url).pipe(
          map(() => new MemberProfileTravellersActions.DestroyProfileTravellerSuccess(action.payload)),
          catchError((error) => of(new MemberProfileTravellersActions.DestroyProfileTravellerFail(error)))
        );
      })
    )
  );

  public destroyProfileTravellerFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MemberProfileTravellersActions.DESTROY_PROFILE_TRAVELLER_FAIL),
        filter((action) => action.error.status === 400),
        tap(() => {
          this.snackBar.openFromComponent(GenericApiErrorComponent, {
            duration: 5000,
            data: this.translateService.instant('ProfileDetails:RemoveTravellerError'),
          });
        })
      ),
    { dispatch: false }
  );

  public updateProfileTraveller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfileTravellersActions.UPDATE_PROFILE_TRAVELLER),
      mergeMap((action: MemberProfileTravellersActions.UpdateProfileTraveller) => {
        const url = `${ApiEndpoint.MEMBER_PROFILE_TRAVELLER}${action.payload.id}/`;

        return this.http.put<IMemberProfileTraveller>(url, action.payload.data).pipe(
          map((updatedTraveller) => new MemberProfileTravellersActions.UpdateProfileTravellerSuccess(updatedTraveller)),
          catchError(() => of(new MemberProfileTravellersActions.UpdateProfileTravellerFail()))
        );
      })
    )
  );

  public updateMultipleProfileTraveller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfileTravellersActions.UPDATE_MULTIPLE_PROFILE_TRAVELLER),
      withLatestFrom(this.store.select(getMemberProfileFeatureState)),
      switchMap(
        ([action, memberProfileState]: [
          MemberProfileTravellersActions.UpdateMultipleProfileTraveller,
          MemberProfileState,
        ]) => {
          const actions = [];

          Object.keys(action.payload).forEach((key) => {
            if (/^traveller\d+$/.test(key) || /^leadTraveller$/.test(key) || /^leadDriver$/.test(key)) {
              const counter = key.match(/traveller(\d)/)?.[1];
              const traveller = memberProfileState.travellers.find((trav) => trav.id === action.payload[key]);
              const birthDate = counter ? action.payload['travellerDOB' + counter] : action.payload.dateOfBirth;
              const payload = {
                id: traveller.id,
                data: {
                  birth_date: moment(birthDate).format('yyyy-MM-DD'),
                  member_profile: memberProfileState.url,
                  citizenship: traveller.citizenship.url,
                  first_name: traveller.first_name,
                  last_name: traveller.last_name,
                  title: traveller.title,
                },
              };
              actions.push(new MemberProfileTravellersActions.UpdateProfileTraveller(payload));
            }
          });

          return of(...actions);
        }
      )
    )
  );
}
